import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import NavigationUtils from 'utils/NavigationUtils';
import _ from 'lodash';

export default class SummaryBox extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        process: PropTypes.object,
        navId: PropTypes.string,
        revisionNumber: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.state = {
            entityName: null,
            entityType: null,
            expanded: true
        };
    }

    getOffsets(inputDatasetName) {
        let offsets = '';
        if (inputDatasetName) {
            const offsetsArray = this.props.process.variable_retrieval.input_datasets[inputDatasetName].offsets;
            offsets = `[${offsetsArray[0]}, ${offsetsArray[1]}]`;
        }
        return offsets;
    }

    entityChanged(entityName, entityType) {
        this.setState({ entityName, entityType });
    }

    renderTwoColumns(label, component) {
        const labelStyle = {
            width: '33%'
        };
        const valueStyle = {
            width: '67%',
            lineHeight: '14px'
        };
        return (
            <Flexbox flexDirection="row" marginBottom="14px">
                <div className="form-label" style={labelStyle}>{`${label}:`}</div>
                <div className="form-value" style={valueStyle}>{component}</div>
            </Flexbox>
        );
    }

    renderInputDatasetDetails() {
        const process = this.props.process;
        const name = this.state.entityName;
        const inputDataset = process.variable_retrieval.input_datasets[name];
        let component = null;
        if (inputDataset) {
            const inputDatastreams = this.renderAsVerticalList(ProcessUtils.getInputDatastreamsForDataset(process, name));
            const numRetrievedVariables = ProcessUtils.getRetrievedVariablesForDataset(process, name).length;

            component = (
                <Flexbox flexDirection="column">
                    {this.renderTwoColumns('Input Datastreams', inputDatastreams)}
                    {this.renderTwoColumns('Retrieved Variables', numRetrievedVariables)}
                </Flexbox>

            );
        }
        return component;
    }

    renderAsVerticalList(list) {
        const items = list.map(itemName => {
            return <div key={itemName} style={{ marginBottom: '4px' }}>{itemName}</div>;
        });
        return (
            <Flexbox flexDirection="column">
                {items}
            </Flexbox>
        );
    }

    renderCoordinateSystemDetails() {
        const process = this.props.process;
        const name = this.state.entityName;
        const coordinateSystem = process.variable_retrieval.coordinate_systems[name];
        let component = null;
        if (coordinateSystem) {
            const dimensionRows = Object.keys(coordinateSystem.dimensions).map((dimensionName, index, keys) => {
                const dimension = coordinateSystem.dimensions[dimensionName];
                const griddingMethod = Constants.getOptionById(dimension.gridding_method, Constants.COORDINATE_SYSTEM_GRIDDING_METHODS).label;
                const transformType = Constants.getOptionById(dimension.data_transformation.transformation_type,
                    Constants.COORDINATE_SYSTEM_TRANSFORM_TYPES).label;

                let subRows = '';
                if (dimension.gridding_method === 'mapping') {
                    subRows = (
                        <div>
                            {this.renderTwoColumns('Gridding Method', griddingMethod)}
                            {this.renderTwoColumns('Transform Type', transformType)}
                            {this.renderTwoColumns('Input Dataset', dimension.input_dataset)}
                        </div>
                    );
                } else if (dimension.gridding_method === 'regular_grid') {
                    subRows = (
                        <div>
                            {this.renderTwoColumns('Gridding Method', griddingMethod)}
                            {this.renderTwoColumns('Transform Type', transformType)}
                            {this.renderTwoColumns('Interval', dimension.data_transformation.interval)}
                            {this.renderTwoColumns('Length', dimension.data_transformation.length)}
                            {this.renderTwoColumns('Alignment', Constants.getOptionById(dimension.data_transformation.bin_alignment, Constants.COORDINATE_SYSTEM_BIN_ALIGNMENTS).label)}
                        </div>
                    );
                } else {
                    subRows = (
                        <div>
                            {this.renderTwoColumns('Gridding Method', griddingMethod)}
                        </div>
                    );
                }
                let dividerComponent = null;
                if (index < keys.length - 1) {
                    dividerComponent = <hr />;
                }

                return (
                    <div key={dimensionName}>
                        <div style={{ marginBottom: '10px' }}>
                            <span className="form-label nowarp">Dimension:</span> {dimensionName}
                        </div>
                        <div>
                            {subRows}
                        </div>
                        {dividerComponent}
                    </div>
                );

            });

            component = (
                <Flexbox flexDirection="column">
                    {dimensionRows}
                </Flexbox>
            );
        }
        return component;
    }

    renderRetrievedVariableDetails() {
        const process = this.props.process;
        const name = this.state.entityName;
        const retrievedVariable = process.variable_retrieval.retrieved_variables[name];

        let component = null;

        if (retrievedVariable) {
            const outputDatastreams = this.renderAsVerticalList(ProcessUtils.getRetrievedVariableOutputDatastreams(process, name));

            // Get the input datastreams for all rules of this variable
            // TODO: we may want to show the data locations as well?
            const inputDatastreams = this.renderAsVerticalList(ProcessUtils.getRetrievedVariableInputDatastreams(process, name));
            const offsets = this.getOffsets(retrievedVariable.input_dataset);
            const required = retrievedVariable.variable_required ? 'Yes' : 'No';

            let qcVariable = 'No';
            qcVariable = retrievedVariable.retrieve_qc_variable ? 'Retrieve if available' : qcVariable;
            component = (
                <Flexbox flexDirection="column">
                    {this.renderTwoColumns('Data Type', retrievedVariable.data_type)}
                    {this.renderTwoColumns('Units',
                        ProcessUtils.getUnits(retrievedVariable.units))}
                    {this.renderTwoColumns('Coordinate System',
                        retrievedVariable.coordinate_system)}
                    {this.renderTwoColumns('Dimensions', ProcessUtils.getRetrievedVariableDimensions(process, name))}
                    {this.renderTwoColumns('Required', required)}
                    {this.renderTwoColumns('Companion QC Variable', qcVariable)}
                    {this.renderTwoColumns('Inputs', inputDatastreams)}
                    {this.renderTwoColumns('Offsets', offsets)}
                    {this.renderTwoColumns('Outputs', outputDatastreams)}
                </Flexbox>

            );

            qcVariable = retrievedVariable.qc_variable_required ? 'Required' : qcVariable;
        }
        return component;
    }

    renderOutputDatastreamDetails() {
        const process = this.props.process;
        const name = this.state.entityName;
        let component = null;
        if (_.includes(process.output_datastreams, name)) {
            const coordinateSystemNames = ProcessUtils.getCoordinateSystemsForOutputDatastream(process, name);
            let coordinateSystemName = '';
            if (coordinateSystemNames.length > 0) {
                coordinateSystemName = coordinateSystemNames[0];
            }

            const outputDatastreamMappings = process.variable_retrieval.output_datastream_variable_mappings[name];
            let mappingsCount = 0;
            if (outputDatastreamMappings) {
                mappingsCount = Object.keys(outputDatastreamMappings).length;
            }

            component = (
                <Flexbox flexDirection="column">
                    {this.renderTwoColumns('Coordinate System', coordinateSystemName)}
                    {this.renderTwoColumns('Mapped Variables', mappingsCount)}
                </Flexbox>

            );

        }
        return component;
    }

    render() {
        let name = this.state.entityName ? this.state.entityName : 'No item selected';
        let entitySummary = '';
        let path = '/';

        switch (this.state.entityType) {
            case Constants.TYPE_INPUT_DATASET:
                entitySummary = this.renderInputDatasetDetails();
                path = `/variable-retrieval/input-dataset/${name}`;
                break;
            case Constants.TYPE_COORDINATE_SYSTEM:
                entitySummary = this.renderCoordinateSystemDetails();
                path = `/variable-retrieval/coordinate-system/${name}`;
                break;
            case Constants.TYPE_RETRIEVED_VARIABLE:
                entitySummary = this.renderRetrievedVariableDetails();
                path = `/variable-retrieval/retrieved-variable/${name}`;
                break;
            case Constants.TYPE_OUTPUT_DATASTREAM:
                entitySummary = this.renderOutputDatastreamDetails();
                path = `/variable-retrieval/output-datastream/${name}`;
                break;
            default:  // stupid linting rule requires a default case
                break;
        }

        if (entitySummary === null) {
            name = 'No item selected';
        }

        const onClick = NavigationUtils.getNavClickFunction(this.props.navId, this.props.revisionNumber, path);
        const title = this.state.entityName ? <a onClick={onClick} title={name}>{name}</a> : name;

        return (
            <Flexbox className="bevel-box summary" flexDirection="column">
                <Flexbox flexDirection="row" justifyContent="flex-start" alignItems="center" paddingBottom="8px">
                    <div className="summary-title">{title}</div>
                </Flexbox>
                <div className="divider" />
                <div style={{ paddingTop: '10px' }}>
                    {entitySummary}
                </div>
            </Flexbox>
        );
    }

}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/SummaryBox.js